import { Button } from '@shared/components/ui/button';
import { Separator } from '@shared/components/ui/shadcn/separator';
import {
  LOGISTIC_PIVOT_DIMENSIONS,
  type LogisticPivotDimension,
  type LogisticPivotGroupDisplayMode,
} from '../../models/LogisticPivot';
import { cn } from '@shared/utils/utils';
import {
  Building2,
  CalendarDays,
  CalendarRange,
  ChevronDown,
  ChevronUp,
  Columns2,
  Globe2,
  GripVertical,
  MapPin,
  Layers,
  ListTree,
  Package,
  Plus,
  Store,
  Truck,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const DIMENSION_ICONS: Record<LogisticPivotDimension, LucideIcon> = {
  year: CalendarRange,
  month: CalendarDays,
  team: Building2,
  shop: Store,
  courier: Truck,
  courier_service: Layers,
  country: Globe2,
  postal_zone: MapPin,
  item: Package,
};

interface DimensionBuilderProps {
  value: LogisticPivotDimension[];
  onChange: (next: LogisticPivotDimension[]) => void;
  groupDisplay: LogisticPivotGroupDisplayMode;
  onGroupDisplayChange: (next: LogisticPivotGroupDisplayMode) => void;
  /** Solo `tenant_3pl`: la agrupación por cliente (team) está disponible. */
  allowTeamDimension?: boolean;
}

export const DimensionBuilder = ({
  value,
  onChange,
  groupDisplay,
  onGroupDisplayChange,
  allowTeamDimension = true,
}: DimensionBuilderProps) => {
  const { t } = useTranslation('logisticPivotModule');

  const dimensionPool = useMemo(
    () =>
      allowTeamDimension
        ? LOGISTIC_PIVOT_DIMENSIONS
        : LOGISTIC_PIVOT_DIMENSIONS.filter((d) => d !== 'team'),
    [allowTeamDimension]
  );

  const labelByDim = useMemo(
    () =>
      LOGISTIC_PIVOT_DIMENSIONS.reduce(
        (acc, d) => {
          acc[d] = t(`dimensions.${d}`);
          return acc;
        },
        {} as Record<LogisticPivotDimension, string>
      ),
    [t]
  );

  const availableToAdd = useMemo(
    () => dimensionPool.filter((d) => !value.includes(d)),
    [dimensionPool, value]
  );

  const canUseHierarchy = value.length > 1;
  const hierarchyDisabled = !canUseHierarchy;

  const remove = (dim: LogisticPivotDimension) => {
    onChange(value.filter((d) => d !== dim));
  };

  const add = (dim: LogisticPivotDimension) => {
    if (!value.includes(dim)) onChange([...value, dim]);
  };

  const move = (index: number, dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    const tmp = next[index];
    next[index] = next[j]!;
    next[j] = tmp!;
    onChange(next);
  };

  return (
    <div
      className="flex max-h-[min(65vh,520px)] flex-col gap-0"
      data-testid="logistic-pivot-dimensions"
    >
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overflow-x-hidden pr-0.5">
        <p className="text-xs leading-relaxed text-muted-foreground">{t('dimensions.grouping_panel_intro')}</p>

        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
            {t('dimensions.view_mode_label')}
          </p>
          <div
            className="flex rounded-lg border border-border bg-muted/20 p-0.5 gap-0.5"
            role="group"
            aria-label={t('dimensions.view_mode_label')}
          >
            <button
              type="button"
              onClick={() => onGroupDisplayChange('columns')}
              className={cn(
                'flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-2 text-xs font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                groupDisplay === 'columns'
                  ? 'bg-background text-foreground shadow-sm ring-1 ring-border/60'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Columns2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="hidden min-[380px]:inline">{t('dimensions.view_columns')}</span>
              <span className="min-[380px]:hidden">{t('dimensions.view_columns_short')}</span>
            </button>
            <button
              type="button"
              disabled={hierarchyDisabled}
              title={hierarchyDisabled ? t('dimensions.view_hierarchy_disabled_hint') : undefined}
              onClick={() => onGroupDisplayChange('hierarchy')}
              className={cn(
                'flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-2 text-xs font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                hierarchyDisabled && 'opacity-50 cursor-not-allowed',
                groupDisplay === 'hierarchy' && !hierarchyDisabled
                  ? 'bg-background text-foreground shadow-sm ring-1 ring-border/60'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <ListTree className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="hidden min-[380px]:inline">{t('dimensions.view_hierarchy')}</span>
              <span className="min-[380px]:hidden">{t('dimensions.view_hierarchy_short')}</span>
            </button>
          </div>
          <p className="text-[11px] leading-snug text-muted-foreground">
            {groupDisplay === 'columns'
              ? t('dimensions.view_columns_hint')
              : t('dimensions.view_hierarchy_hint')}
          </p>
        </div>

        <Separator className="bg-border/80" />

        <div>
          <div className="mb-2 flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-foreground">
              {t('dimensions.section_order')}
            </span>
            {value.length > 0 && (
              <span className="text-[11px] text-muted-foreground">{t('dimensions.section_order_hint')}</span>
            )}
          </div>

          {value.length > 0 ? (
            <ul className="space-y-1.5" role="list">
              {value.map((dim, index) => {
                const Icon = DIMENSION_ICONS[dim];
                return (
                  <li
                    key={dim}
                    className="flex items-center gap-2 rounded-lg border border-border bg-muted/25 px-2 py-2 transition-colors"
                  >
                    <span
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background text-xs font-semibold tabular-nums text-muted-foreground shadow-sm ring-1 ring-border/60"
                      aria-hidden
                    >
                      {index + 1}
                    </span>
                    <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/50 hidden sm:block" aria-hidden />
                    <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                    <span className="min-w-0 flex-1 text-sm font-medium text-foreground truncate">
                      {labelByDim[dim]}
                    </span>
                    <div className="flex shrink-0 items-center gap-0.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={index === 0}
                        onClick={() => move(index, -1)}
                        aria-label={t('dimensions.move_up')}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={index === value.length - 1}
                        onClick={() => move(index, 1)}
                        aria-label={t('dimensions.move_down')}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => remove(dim)}
                        aria-label={t('dimensions.remove', { label: labelByDim[dim] })}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="rounded-lg border border-dashed border-border bg-muted/15 px-3 py-6 text-center">
              <p className="text-sm text-muted-foreground">{t('dimensions.empty_axes_message')}</p>
            </div>
          )}
        </div>

        {availableToAdd.length > 0 && (
          <>
            <Separator className="bg-border/80" />
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground">
                {t('dimensions.section_add')}
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {availableToAdd.map((dim) => {
                  const Icon = DIMENSION_ICONS[dim];
                  return (
                    <Button
                      key={dim}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-auto justify-start gap-2 border-dashed py-2.5 text-left font-normal transition-colors hover:border-primary/40 hover:bg-muted/40"
                      onClick={() => add(dim)}
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted/50">
                        <Icon className="h-4 w-4 text-foreground" aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1 text-sm font-medium leading-snug">{labelByDim[dim]}</span>
                      <Plus className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    </Button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
